import { type DateValue, getLocalTimeZone, today } from "@internationalized/date";
import { Plus } from "@untitledui/icons";
import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import GridLayout from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import type { AnalyticsView } from "@/components/AnalyticsViewConfigurationModal";
import { AnalyticsViewDisplay } from "./AnalyticsViewDisplay";
import { WidgetHeader } from "./WidgetHeader";

export interface LayoutItem {
  i: string; // viewId
  x: number;
  y: number;
  w: number;
  h: number;
}

interface DashboardContentProps {
  views: AnalyticsView[];
  editable?: boolean;
  onAddView?: () => void;
  layout?: LayoutItem[];
  onLayoutChange?: (layout: LayoutItem[]) => void;
  onEditWidget?: (viewId: string) => void;
  onDeleteWidget?: (viewId: string) => void;
  timePeriod?: "daily" | "weekly" | "monthly";
  dateRange?: { start: DateValue; end: DateValue } | null;
  timeGranularity?: "day" | "week" | "month";
}

export const DashboardContent: React.FC<DashboardContentProps> = ({
  views,
  editable = false,
  onAddView,
  layout,
  onLayoutChange,
  onEditWidget,
  onDeleteWidget,
  timePeriod = "weekly",
  dateRange = null,
  timeGranularity = "week",
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(1200);

  // Default to last 31 days if no dateRange is provided
  const effectiveDateRange = useMemo(() => {
    if (dateRange) return dateRange;
    const end = today(getLocalTimeZone());
    const start = end.subtract({ days: 31 });
    return { start, end };
  }, [dateRange]);

  // Measure container width
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  // Calculate grid cell size based on container width
  // Account for margins between columns: (containerWidth - (11 gaps * 16px margin)) / 12 columns
  const horizontalMargin = 16;
  const cols = 12;
  const columnWidth = (containerWidth - (cols - 1) * horizontalMargin) / cols;
  const rowHeight = 100; // Fixed row height

  // Generate default layout if not provided
  const defaultLayout: LayoutItem[] = useMemo(() => {
    if (layout && layout.length > 0) return layout;

    return views.map((view, index) => ({
      i: view.id,
      x: 0,
      y: index * 4, // Stack vertically
      w: 12, // Full width (12 columns)
      h: 4, // 4 rows high
    }));
  }, [views, layout]);

  return (
    <div ref={containerRef} className="w-full flex flex-col gap-8">
      {/* Global styles for grid items - always disable transitions */}
      <style>
        {`
          /* Disable all animations on grid items */
          .react-grid-item {
            transition: none !important;
          }
        `}
      </style>

      {/* Grid Layout for Analytics Views */}
      {views.length > 0 && (
        <div className="relative">
          {/* Grid overlay - shown during drag in edit mode */}
          {editable && (
            <style>
              {`
                .react-grid-item.react-draggable-dragging,
                .react-grid-item.react-grid-placeholder {
                  background: transparent !important;
                }

                /* Grid overlay pattern */
                .react-grid-layout {
                  background-image:
                    linear-gradient(to right, rgba(156, 163, 175, 0.15) 1px, transparent 1px),
                    linear-gradient(to bottom, rgba(156, 163, 175, 0.15) 1px, transparent 1px);
                  background-size: ${columnWidth + horizontalMargin}px ${rowHeight + 16}px;
                }

                /* Plus pattern at grid intersections */
                .react-grid-layout::before {
                  content: '';
                  position: absolute;
                  top: 0;
                  left: 0;
                  right: 0;
                  bottom: 0;
                  background-image:
                    radial-gradient(circle at center, rgba(156, 163, 175, 0.3) 2px, transparent 2px);
                  background-size: ${columnWidth + horizontalMargin}px ${rowHeight + 16}px;
                  background-position: 0 0;
                  pointer-events: none;
                  opacity: 0;
                  transition: opacity 0.2s;
                }

                /* Show grid overlay when dragging */
                .react-grid-layout.react-grid-layout--dragging::before {
                  opacity: 1;
                }
              `}
            </style>
          )}

          <GridLayout
            className="layout"
            layout={defaultLayout}
            cols={12}
            rowHeight={rowHeight}
            width={containerWidth}
            margin={[16, 16]}
            containerPadding={[0, 0]}
            isDraggable={editable}
            isResizable={editable}
            compactType="vertical"
            preventCollision={false}
            useCSSTransforms={false}
            onLayoutChange={(newLayout) => {
              if (onLayoutChange) {
                onLayoutChange(newLayout as LayoutItem[]);
              }
            }}
            draggableHandle=".drag-handle"
          >
            {views.map((view) => {
              return (
                <div key={view.id} className={"h-full"}>
                  <div className="h-full rounded-lg border border-gray-200 overflow-hidden flex flex-col">
                    {/* Widget Header - only shown in edit mode */}
                    {editable && (
                      <WidgetHeader
                        viewId={view.id}
                        viewName={view.name}
                        editable={editable}
                        onEdit={() => onEditWidget?.(view.id)}
                        onDelete={() => onDeleteWidget?.(view.id)}
                      />
                    )}

                    {/* Analytics View Content */}
                    <div className="flex-1 min-h-0">
                      <AnalyticsViewDisplay
                        view={view}
                        timePeriod={timePeriod}
                        dateRange={effectiveDateRange}
                        timeGranularity={timeGranularity}
                        showTitle={!editable}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </GridLayout>
        </div>
      )}

      {/* Add Analytics View Button - only shown in editable mode */}
      {editable && onAddView && (
        <button
          type="button"
          onClick={onAddView}
          className="w-full h-[282px] p-3 bg-[#f9f5ff] rounded-md inline-flex justify-center items-center gap-4 overflow-hidden hover:opacity-80 transition-opacity"
        >
          <div className="w-8 h-8 relative bg-[#f4ebff] rounded-full overflow-hidden flex items-center justify-center">
            <Plus className="w-4 h-4 text-brand-600" />
          </div>
          <div className="text-center justify-start text-primary text-base font-semibold leading-normal">
            Add Analytics View
          </div>
        </button>
      )}

      {/* Empty state - shown when no views and not editable */}
      {!editable && views.length === 0 && (
        <div className="w-full p-8 bg-white rounded-lg border border-gray-200">
          <div className="text-center text-gray-500">
            <p className="text-lg font-medium mb-2">No views added yet</p>
            <p className="text-sm">Edit this dashboard to add analytics views</p>
          </div>
        </div>
      )}
    </div>
  );
};
